/**
 * Clearline redesign of the cases list — the NEW design, mounted at
 * /redesign/cases so it can be compared against the existing list at /cases.
 *
 * Imported from the wip/updatedui-redesign branch, where it was CasesModern.js
 * (a file the dev merge had deleted on this branch). Unlike the redesigned
 * detail page it needs no isolated component copies — it depends only on
 * services and assets that both designs share.
 *
 * It calls getAssessmentList(), so it inherits the per-client API dispatch and
 * therefore works for Hegic as well as Bajaj/Ergo.
 *
 * `detailsBasePath` is passed by the route so rows link to the redesigned
 * detail page, keeping the new design self-consistent when navigating.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { caseHrefFor, withCountryParam } from "../utils/caseHref";
import {
  dedupeRows,
  mergeRefreshedPage,
  readListCache,
  writeListCache,
} from "./listCache";
import {
  clampOffset,
  readOffset,
  useScrollMemory,
  writeOffset,
} from "../hooks/useScrollMemory";
import {
  AlertTriangle,
  ChevronDown,
  Search,
  X,
} from "lucide-react";
import {
  getApiGeneration,
  getAssessmentList,
  getAssessmentReport,
  searchCases,
} from "../services/apiService";
import {
  getStatusInfo,
  getStatusInfoFor,
  getClearlineStatusChip,
  formatClearlineDateTime,
} from "../utils/statusMapping";
import { usePageConfig } from "../hooks/usePageConfig";

// Local asset imports - Vehicle images

// Local asset imports - Icons
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";
import loadingIcon from "../assets/icons/cases/loading.svg";
import { getMediaBaseUrl, getClientEntry } from "../config/clientConfig";
import CaseThumb from "../components/CaseThumb";
import {
  MOTOR_SIDES,
  pickListImage,
  placeholderFor,
} from "../utils/caseThumbnail";
import { mediaResolverFor } from "../utils/mediaResolver";
import { defaultListRange, newestFirst, withinListRange } from "../utils/defaultDateRange";
import { getSourceName } from "../services/authService";
import { summarizeCaseForCammy } from "../utils/cammyCaseSummary";
import { useCammySay } from "../context/CammySpeechContext";

// Flag components using local SVG assets — Clearline sizing: 18x13, radius 2px.
const flagClass =
  "h-[13px] w-[18px] flex-none rounded-[2px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]";

const LvLatvia = () => (
  <img src={latviaFlag} alt="Latvia flag" className={flagClass} />
);

const LtLithuania = () => (
  <img src={lithuaniaFlag} alt="Lithuania flag" className={flagClass} />
);

const EeEstonia = () => (
  <img src={estoniaFlag} alt="Estonia flag" className={flagClass} />
);

// Warning codes list
const WARNING_CODES = [
  { code: "All", description: "All Warnings" },
  { code: "W001", description: "Partial side image" },
  { code: "W002", description: "Not a passenger car" },
  { code: "W003", description: "Incorrect side captured" },
  { code: "W004", description: "VIN not readable" },
  { code: "W005", description: "ODO meter not readable" },
  { code: "W006", description: "Number plate not readable" },
  { code: "W007", description: "Location changed" },
  { code: "W008", description: "No location permission" },
  { code: "W009", description: "Vehicle far away" },
  { code: "W010", description: "Low light environment" },
  { code: "W011", description: "Image blurry" },
  { code: "W012", description: "Screen images detected" },
  { code: "W013", description: "Multiple cars detected" },
  { code: "W014", description: "Commercial/2-Wheeler/3-Wheeler" },
  { code: "W015", description: "Minimum required images missing" },
  { code: "W016", description: "Reflection/Glare/Shadow detected" },
  { code: "W017", description: "Dust/Mud detected" },
  { code: "W018", description: "Covered with snow/sleet" },
  { code: "W019", description: "Rain/Rain droplets detected" },
  { code: "W020", description: "Obstruction in image" },
  { code: "W021", description: "Low resolution image" },
  { code: "W022", description: "Not an outside windshield" },
  { code: "W023", description: "VIN mismatch" },
  { code: "W024", description: "Registration number mismatch" },
  { code: "W025", description: "Incorrect images" },
  { code: "W026", description: "Low internet bandwidth" },
  { code: "W027", description: "Inspection outside country" },
];

export default function CasesModern({
  detailsBasePath = "/cases-modern",
} = {}) {
  console.log("🏁 CasesModern component mounting/rendering...");

  const { pageConfig } = usePageConfig();

  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [showAllWarningsModal, setShowAllWarningsModal] = useState(false);
  const [allWarningsData, setAllWarningsData] = useState([]);
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // 🔧 PAGINATION MODE TOGGLE 🔧
  // Set to true for INFINITE SCROLL, false for PAGINATION with prev/next buttons
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(true); // Change to false to use pagination

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15); // Fixed at 15 items per page
  const [totalPages, setTotalPages] = useState(1);

  // Infinite scroll states
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const currentPageRef = useRef(1); // Use ref to track current page for API calls

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedCountry, setSelectedCountry] = useState("All");
  const [selectedWarning, setSelectedWarning] = useState("All");
  // Opens on the same window as the existing design — the last 30 days —
  // rather than on "" and a hidden fallback. The chip then says what is
  // actually being asked for, and a cleared filter genuinely means all dates.
  const [startDate, setStartDate] = useState(
    () => defaultListRange().startDate,
  );
  const [endDate, setEndDate] = useState(() => defaultListRange().endDate);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Dropdown states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showWarningDropdown, setShowWarningDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Assessment report states
  const [reportData, setReportData] = useState({
    totalInspections: 0,
    completedInspections: 0,
    incorrectImages: 0,
  });
  const [reportLoading, setReportLoading] = useState(false);

  // Refs for click outside detection
  const statusDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const warningDropdownRef = useRef(null);
  const selectedCountryRef = useRef("All");
  const selectedStatusRef = useRef("All");
  /**
   * The window the filter currently shows, readable from inside fetchCases.
   *
   * fetchCases is memoised once (its dependency list is deliberately short),
   * so reading startDate/endDate directly there sees the values from first
   * render. Page-two loads and the poller pass no range and rely on this —
   * the same reason the country, status and warning filters keep refs.
   */
  const dateWindowRef = useRef({ startDate, endDate });
  useEffect(() => {
    dateWindowRef.current = { startDate, endDate };
  }, [startDate, endDate]);
  const selectedWarningRef = useRef("All");
  const datePickerRef = useRef(null);
  const warningsScrollRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Polling ref for processing cases
  const pollingIntervalRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
      }
      if (
        warningDropdownRef.current &&
        !warningDropdownRef.current.contains(event.target)
      ) {
        setShowWarningDropdown(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper functions with useCallback to fix dependency warning

  /**
   * The photograph that stands for a row.
   *
   * The existing design's rule, adopted here as the default because it is the
   * one every client's list has actually been seen working against: the sides
   * the client prefers, then the front and its three-quarter views in a fixed
   * order, then any side at all, then whatever other image field the row
   * carries. A side is accepted as an array, a bare object or a string —
   * Bajaj's list sends `{ front: { picture } }` where Ergo's sends an array,
   * and requiring the array is why Bajaj's rows showed the placeholder here
   * while the existing design showed the car.
   *
   * URLs go through the client's media resolver rather than a string join:
   * Bajaj's pictures arrive absolute, and its relative paths need re-rooting
   * under /root for the bucket that serves them. The resolver knows both, and
   * the join knew neither.
   */
  const getVehicleImageFromAPI = useCallback((assessment) => {
    const client = getClientEntry(getSourceName());
    const toUrl = mediaResolverFor();

    const sides = client?.listImageSides || MOTOR_SIDES;
    const fromSides = pickListImage(assessment?.car_images, sides);
    if (fromSides) return toUrl(fromSides);

    // Rows with no car_images: a direct field, then the uploads.
    const directFields = [
      "thumbnail_image",
      "preview_image",
      "main_image",
      "vehicle_image",
      "image_url",
      "picture",
      "upload_picture",
    ];
    for (const field of directFields) {
      const value = assessment?.[field];
      if (typeof value === "string" && value.trim()) return toUrl(value.trim());
    }
    const uploads = Array.isArray(assessment?.uploads)
      ? assessment.uploads
      : [];
    for (const upload of uploads) {
      const path = upload?.upload_picture || upload?.picture;
      if (typeof path === "string" && path.trim()) return toUrl(path.trim());
    }

    /**
     * No photograph on the case means no photograph shown.
     *
     * This used to pick one of seven stock car photographs by the case id,
     * which does not read as "no image" — it reads as this claim's vehicle.
     * A line drawing reads as an absence, which is what it is.
     */
    return placeholderFor(client?.inspectionLine);
  }, []);

  const formatCaseId = useCallback((caseId) => {
    if (!caseId || caseId === "N/A") return "N/A";

    // Return the full case ID without truncation
    return caseId;
  }, []);

  // New function to format display ID - prioritize vehicle registration number
  // The registration is the primary identifier in the Case ID column, with the
  // reference number beneath it. Returns "" when the plate is unusable so the
  // caller can fall back to the reference number.
  const getPlate = useCallback((assessment) => {
    const plate = assessment?.num_plate;
    if (!plate || plate === "N/A" || !plate.trim()) return "";
    return plate.trim();
  }, []);

  const getStatusIcon = useCallback((status) => {
    const statusInfo = getStatusInfo(status);
    return statusInfo.icon;
  }, []);

  // getUniqueStatuses() hands back display text ("Review Completed"), and the
  // status filter dropdown wants that row's own icon — getStatusIcon needs
  // the numeric claim_state back. Copied verbatim from Cases.js's
  // getStatusCodeFromText (including its default-to-0 for any text it
  // doesn't name) so the redesign's dropdown icon matches current design's
  // exactly, gap and all, rather than this file inventing its own mapping.
  const getStatusCodeFromText = useCallback((statusText) => {
    switch (statusText) {
      case "In-Process":
        return 0;
      case "AI Assessed":
        return 3;
      case "Under Review":
        return 5;
      case "Review Completed":
        return 8;
      case "Failed":
        return 6;
      default:
        return 0;
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    const statusInfo = getStatusInfo(status);
    return statusInfo.color;
  }, []);

  const formatSideName = (side) => {
    if (!side) return "";
    const withSpaces = side.replace(/([a-z])([A-Z])/g, "$1 $2");
    const cleaned = withSpaces.replace(/_/g, " ");
    return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const aicamWarningMessages = {
    "Low light": "Image is taken in a possibly low light environment",
    "Obstructed image": "There is possibly an obstruction in the image",
    "Sleet image": "Car is possibly covered with sleet",
    "Raindrops image": "Rain/Rain Droplets detected on car surface",
    "Blur image": "Image is possibly blurry",
    "Dirty image": "The system has detected that the image possibly has dirt",
    "Far image": "The vehicle seems far away",
    "Screen image": "Screen images detected",
  };

  const activeClient = getClientEntry(getSourceName());
  // Some lists carry no image at all — property's rows and Hegic's alike — so
  // those rows fetch their own. Declared per client rather than inferred, so a
  // list that starts carrying a cover image just drops the key.
  const selfFetchesThumbnail = Boolean(activeClient?.listThumbnail);

  // Warnings a client's reviewers don't want offered in the dropdown at all
  // (still shown on a row if a case happens to carry one) — same idea as
  // Cases.js, applied to WARNING_CODES' hardcoded copy instead of the config
  // list, since this file doesn't read filtersConfig for it.
  const hiddenWarningCodes = new Set(activeClient?.hiddenWarningCodes || []);
  const visibleWarningCodes = WARNING_CODES.filter(
    (warning) => !hiddenWarningCodes.has(warning.code),
  );

  /**
   * Columns a client has no data for.
   *
   * Hegic's list rows are id, ref_num, claim_state and created_at — there is
   * no country in them, and getAssessmentListV2 does not send countryname
   * either, so the column was always blank and the filter could never have
   * applied. Hiding both is one key on the client rather than a check per
   * place, and the existing design already honours the same idea through
   * table.columns.<name>.enabled.
   */
  const hiddenFields = new Set(activeClient?.hiddenListFields || []);
  const showCountry = !hiddenFields.has("country");
  const showWarningsColumn = !hiddenFields.has("warnings");

  /**
   * Filters a client cannot usefully drive, with the column left alone.
   *
   * Distinct from hiddenListFields, which says the client has no data for a
   * field at all and hides the column with it. A row can carry warnings worth
   * showing while the filter over them is meaningless — the codes it lists are
   * not every client's.
   */
  const hiddenFilters = new Set(activeClient?.hiddenListFilters || []);
  const showWarningsFilter = showWarningsColumn && !hiddenFilters.has("warnings");

  /**
   * Come back to where you were.
   *
   * Keyed by the route and the client so one list never restores another's
   * offset, and only restored once the rows are back — before that the
   * container has no height and the position would clamp to nothing.
   */
  const scrollKey = `${detailsBasePath}:${activeClient?.sourceName || "default"}`;
  useScrollMemory({
    elementId: "cases-list-container",
    key: scrollKey,
    ready: !loading && filteredCases.length > 0,
  });

  const rememberScroll = useCallback(() => {
    const element = document.getElementById("cases-list-container");
    if (element) writeOffset(scrollKey, element.scrollTop);
  }, [scrollKey]);

  /**
   * Cammy narrates whichever row is under the pointer. A click pins the same
   * summary for a fixed window instead of just "until the mouse leaves" — the
   * row's Link opens the case in a new tab, so this list stays mounted and
   * the window runs to completion either way, unlike a hover that a route
   * change could otherwise cut off mid-sentence.
   */
  const { say: sayToCammy, clear: clearCammy } = useCammySay();
  // leanY (the row's own vertical centre) is optional context the dock uses
  // to glance up/down toward it — see CammyDock.jsx's lean effect. Passing it
  // is harmless if the dock's lean feature is ever reverted: an unknown
  // option on `say()` is simply ignored.
  const handleRowHoverStart = useCallback(
    (caseItem, event) => {
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      sayToCammy(summarizeCaseForCammy(caseItem), {
        source: "hover",
        leanY: rect ? rect.top + rect.height / 2 : undefined,
      });
    },
    [sayToCammy],
  );
  const handleRowHoverEnd = useCallback(() => clearCammy("hover"), [clearCammy]);
  const handleRowClick = useCallback(
    (caseItem, event) => {
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      sayToCammy(summarizeCaseForCammy(caseItem), {
        source: "click",
        seconds: 5,
        leanY: rect ? rect.top + rect.height / 2 : undefined,
      });
    },
    [sayToCammy],
  );
  // Leaving the list (e.g. a sidebar click) shouldn't leave a hover summary
  // stuck on screen. A click's summary is intentionally left alone — it has
  // its own 5-second timer and is meant to survive the new tab opening.
  useEffect(() => () => clearCammy("hover"), [clearCammy]);

  /**
   * Column headings, per client.
   *
   * The defaults describe a vehicle. A property claim has no plate and no
   * vehicle — it is identified by its inspection reference and pictured by its
   * building — so those two headings would be describing the wrong thing.
   */
  const columnLabels = {
    vehicle: "Vehicle",
    status: "Status",
    // Every client calls it this: a plate is a motor idea, and half the
    // column's values are reference numbers even for motor.
    caseId: "Inspection ID",
    country: "Country",
    warnings: "Warnings",
    timestamp: "Received",
    ...(activeClient?.listColumnLabels || {}),
  };

  /**
   * What identifies a row for this client.
   *
   * A motor claim is recognised by its registration. A property claim has no
   * vehicle at all — no make, model or plate, and car_images always empty — and
   * is recognised by its address. The field is named in the client's own file
   * (`listIdentityField`), so this falls through to the plate for everyone who
   * does not declare one.
   */
  const identityOf = useCallback((assessment) => {
    const field = getClientEntry(getSourceName())?.listIdentityField;
    const value = field ? assessment?.[field] : null;
    return typeof value === "string" && value.trim() ? value.trim() : null;
  }, []);

  const getWarnings = useCallback(
    (assessment) => {
      const baseUrl = getMediaBaseUrl();
      const newIssues = [];

      // Check for new all_warning_list structure first
      if (
        assessment?.all_warning_list &&
        Array.isArray(assessment.all_warning_list)
      ) {
        console.log(
          "Processing all_warning_list:",
          assessment.all_warning_list,
        );

        assessment.all_warning_list.forEach((warningItem) => {
          if (!warningItem.warning_message) return;

          // Check if warning has images
          if (
            warningItem.images_list &&
            typeof warningItem.images_list === "object" &&
            Object.keys(warningItem.images_list).length > 0
          ) {
            const sidesData = Object.entries(warningItem.images_list).map(
              ([path, side]) => ({
                name: formatSideName(side),
                imageUrl: `${baseUrl}${path}`,
              }),
            );
            newIssues.push({
              message: warningItem.warning_message,
              sidesData,
            });
          } else {
            // Warning without images
            newIssues.push({
              message: warningItem.warning_message,
              sidesData: null,
            });
          }
        });

        // Return warnings if found
        if (newIssues.length > 0) {
          return newIssues;
        }
      }

      // Fallback to old warnings structure for backward compatibility
      if (!assessment || !assessment.warnings) {
        console.log("No warnings data available for assessment:", assessment);
        return [{ message: "No warnings", hasImage: false }];
      }

      const warning = assessment.warnings;

      // VIN validation
      if (warning.vin_valid === false) {
        newIssues.push({ message: "The system couldn't read the VIN" });
      }

      // Number plate validation
      if (warning.num_plate_valid === false) {
        newIssues.push({ message: "Number plate not readable" });
      }

      // Location changed
      if (warning.location_changed) {
        newIssues.push({ message: "Location changed" });
      }

      // Location permission
      if (warning.location_given === false) {
        newIssues.push({ message: "User did not provide location permission" });
      }

      // Partial sides
      if (
        warning.partial_sides &&
        Array.isArray(warning.partial_sides) &&
        warning.partial_sides.length > 0
      ) {
        const sides = warning.partial_sides.map(formatSideName);
        newIssues.push({
          message: `${sides.join(", ")} side image${
            sides.length > 1 ? "s are" : " is"
          } partial`,
          sidesData: null,
        });
      }

      // Incorrect sides
      if (
        warning.incorrect_sides &&
        typeof warning.incorrect_sides === "object" &&
        Object.keys(warning.incorrect_sides).length > 0
      ) {
        const sidesData = Object.entries(warning.incorrect_sides).map(
          ([path, side]) => ({
            name: formatSideName(side),
            imageUrl: `${baseUrl}${path}`,
          }),
        );
        newIssues.push({
          message: "Incorrect side captured: ",
          sidesData,
        });
      }

      // Non-car images
      if (
        warning.non_car &&
        typeof warning.non_car === "object" &&
        Object.keys(warning.non_car).length > 0
      ) {
        const sidesData = Object.entries(warning.non_car).map(
          ([path, side]) => ({
            name: formatSideName(side),
            imageUrl: `${baseUrl}${path}`,
          }),
        );
        newIssues.push({
          message: "Image captured is not a passenger car: ",
          sidesData,
        });
      }

      // AI Cam warnings
      if (
        warning.aicam_warnings &&
        typeof warning.aicam_warnings === "object" &&
        Object.keys(warning.aicam_warnings).length > 0
      ) {
        Object.entries(warning.aicam_warnings).forEach(
          ([path, warningType]) => {
            const message =
              aicamWarningMessages[warningType] || "AI detected issue";
            newIssues.push({
              message,
              sidesData: [{ name: warningType, imageUrl: `${baseUrl}${path}` }],
            });
          },
        );
      }

      // Return no warnings if none exist
      if (newIssues.length === 0) {
        return [{ message: "No warnings", hasImage: false }];
      }

      return newIssues;
    },
    [formatSideName, aicamWarningMessages],
  );

  const getCountryFlag = useCallback((countryName) => {
    const country = countryName?.toLowerCase().trim();

    switch (country) {
      case "latvia":
      case "lv":
      case "lat":
        return <LvLatvia />;
      case "lithuania":
      case "lt":
      case "ltu":
        return <LtLithuania />;
      case "estonia":
      case "ee":
      case "est":
        return <EeEstonia />;
      default:
        console.log("No flag match found for country:", countryName);
        return <LvLatvia />; // Default flag
    }
  }, []);

  const getCountryApiValue = useCallback((countryName) => {
    const country = countryName?.toLowerCase().trim();

    switch (country) {
      case "latvia":
      case "lv":
      case "lat":
        return "latvia";
      case "lithuania":
      case "lt":
      case "ltu":
        return "lithuania";
      case "estonia":
      case "ee":
      case "est":
        return "estonia";
      default:
        return "";
    }
  }, []);

  // Maps the dropdown label to the claim_state value(s) /assessment_list/all
  // filters on server-side — copied verbatim from Cases.js so both designs
  // scope the list the same way instead of filtering only whatever page is
  // already loaded in memory (see fetchCases below).
  const getStatusApiValue = useCallback((status) => {
    switch (status) {
      case "In-Process":
        return "0,1,2";
      case "AI Assessed":
        return "3";
      case "Under Review":
        return "5";
      case "Failed":
        return "6";
      case "Awaiting Retry":
        return "7";
      case "Review Completed":
        return "8";
      default:
        return "";
    }
  }, []);

  // Function to open all warnings modal
  const openAllWarningsModal = useCallback((warnings) => {
    setAllWarningsData(warnings);
    setShowAllWarningsModal(true);
  }, []);

  // Function to close all warnings modal
  const closeAllWarningsModal = useCallback(() => {
    setShowAllWarningsModal(false);
    setAllWarningsData([]);
    setSelectedModalImage(null);
    setSavedScrollPosition(0);
  }, []);

  const [error, setError] = useState(null);

  /**
   * Fetch the header stats — total/completed/failed for the filtered set.
   *
   * This used to go through getCachedAssessmentReport(), shared with the
   * sidebar's Cases count pill. That call takes no parameters, so it always
   * fell through to getAssessmentReport()'s own hardcoded default window
   * (2025-06-15 to today) — the header showed a since-June-2025 total no
   * matter what date range the list itself was filtered to, and never
   * changed again after the one fetch on mount (nothing here re-ran it).
   *
   * Cases.js (the existing design) calls getAssessmentReport() directly with
   * the same start_date/stop_date the list is asking for, and refetches
   * whenever the filters change — same API, same params, same response
   * fields (total ?? total_claims / completed / failed), copied here so the
   * two designs' header numbers agree with their own lists rather than with
   * each other's cache. The sidebar pill is untouched — it is a different
   * page's number and out of scope here.
   */
  const fetchAssessmentReport = useCallback(
    async (dateRange = null) => {
      setReportLoading(true);
      try {
        // v2 (Hegic): /assessment_report answers a different total for every
        // window it's asked about — probed directly against this account,
        // scoped to the list's own last-30-days default it answered
        // total_claims: 44/completed: 20/failed: 23; asked with no window at
        // all (source_name only — the same call the live Hegic portal itself
        // makes for this tile) it answers 1439/1171/243. The list endpoint's
        // own pagination only ever surfaces a much smaller working set (98
        // rows) before 404ing, so it can't stand in for a lifetime count
        // either. The unscoped call is the number the existing Hegic portal
        // shows, so that's what this asks for — a lifetime total, not a
        // claim that it matches whatever page of the list is on screen,
        // which is why the footer hides "of Y" for v2 (see rangeEnd below).
        if (getApiGeneration() === "v2") {
          const response = await getAssessmentReport({});
          setReportData({
            totalInspections: parseInt(response?.total ?? response?.total_claims, 10) || 0,
            completedInspections: parseInt(response?.completed, 10) || 0,
            incorrectImages: parseInt(response?.failed, 10) || 0,
          });
          return;
        }

        const shown = dateWindowRef.current;
        const range =
          dateRange?.startDate && dateRange?.endDate
            ? dateRange
            : shown.startDate && shown.endDate
              ? { startDate: shown.startDate, endDate: shown.endDate }
              : null;

        const params = {};
        if (range) {
          params.start_date = range.startDate;
          params.stop_date = range.endDate;
        }

        const activeCountry = getCountryApiValue(selectedCountryRef.current);
        if (activeCountry) {
          params.country_id = activeCountry;
          params.countryname = activeCountry;
        }

        const activeWarning = selectedWarningRef.current;
        if (activeWarning && activeWarning !== "All") {
          params.warning_id = activeWarning;
        }

        // Same status scoping as Cases.js: the report endpoint's own `status`
        // param, not the list's `claim_state`. "In-Process" is really three
        // claim_state codes (0,1,2), but /assessment_report answers the exact
        // same total/completed/failed for all three — confirmed directly:
        // three separate calls with status=0, status=1, status=2 came back
        // with the identical total_claims. Summing them (the previous
        // approach) tripled the count instead of adding it up, so this sends
        // only the first code.
        const apiStatus = getStatusApiValue(selectedStatusRef.current);
        if (apiStatus) {
          params.status = apiStatus.includes(",") ? apiStatus.split(",")[0] : apiStatus;
        }
        const response = await getAssessmentReport(params);
        const reportResponse = {
          total: parseInt(response?.total ?? response?.total_claims, 10) || 0,
          completed: parseInt(response?.completed, 10) || 0,
          failed: parseInt(response?.failed, 10) || 0,
        };

        setReportData({
          totalInspections: reportResponse.total,
          completedInspections: reportResponse.completed,
          incorrectImages: reportResponse.failed,
        });
      } catch (error) {
        console.error("Error fetching assessment report:", error);
        setReportData({
          totalInspections: 0,
          completedInspections: 0,
          incorrectImages: 0,
        });
      } finally {
        setReportLoading(false);
      }
    },
    [getCountryApiValue, getStatusApiValue],
  );

  // Fetch cases data from API with pagination or infinite scroll
  const fetchCases = useCallback(
    async (
      page = 1,
      dateRange = null,
      isLoadingMore = false,
      isSilent = false,
      // Refreshing a restored list: fold the fresh page into what is on
      // screen instead of appending it or replacing everything with it.
      isRevalidate = false,
    ) => {
      try {
        if (dateRange) {
          console.log("Date range filter:", dateRange);
        }

        // Don't show loading states during silent polling
        if (!isSilent) {
          if (isLoadingMore) {
            setIsLoadingMore(true);
          } else {
            setLoading(true);
          }
        }

        // Build API parameters
        const apiParams = {
          per_page_limit: 15, // Consistent page size
        };

        // Add pagination parameter
        if (isLoadingMore) {
          // For infinite scroll loading more, increment the page number
          currentPageRef.current = currentPageRef.current + 1;
          apiParams.page_num = currentPageRef.current;
          console.log("Loading page:", apiParams.page_num);
        } else {
          // For initial load or date filter changes, reset to page 1
          currentPageRef.current = 1;
          apiParams.page_num = 1;
          console.log("Initial/Reset load - page:", apiParams.page_num);
        }

        /**
         * The window to ask for: the caller's, else whatever the filter
         * shows. Page-two loads and the poller pass nothing, and used to fall
         * through to a hardcoded 2025-06-15 — so the first page was the
         * oldest cases the backend has, under a chip that said "All dates",
         * with photographs that were purged long ago. No window at all now
         * means exactly that, and the API answers newest first.
         */
        const shown = dateWindowRef.current;
        const range =
          dateRange?.startDate && dateRange?.endDate
            ? dateRange
            : shown.startDate && shown.endDate
              ? { startDate: shown.startDate, endDate: shown.endDate }
              : null;
        if (range) {
          apiParams.start_date = range.startDate;
          apiParams.stop_date = range.endDate; // API expects 'stop_date', not 'end_date'
          console.log("Date window for API call:", range);
        }

        const activeCountry = getCountryApiValue(selectedCountryRef.current);
        if (activeCountry) {
          // country_id, not countryname — confirmed against a live request:
          // the backend filters on country_id and ignores an empty countryname.
          apiParams.country_id = activeCountry;
          console.log("Adding country filter:", activeCountry);
        }

        const activeStatus = getStatusApiValue(selectedStatusRef.current);
        if (activeStatus) {
          apiParams.claim_state = activeStatus;
          console.log("Adding status filter:", activeStatus);
        }

        // Use a ref here because fetchCases is memoized and reused by polling/scroll handlers.
        const activeWarning = selectedWarningRef.current;
        if (activeWarning && activeWarning !== "All") {
          apiParams.warning_id = activeWarning;
          console.log("Adding warning filter:", activeWarning);
        }

        // For simplicity, let's fetch a larger dataset and implement client-side pagination
        // You can modify this to use server-side pagination if the API supports offset/page parameters
        const response = await getAssessmentList(apiParams);

        if (response && response.data) {
          console.log("Response data:", response.data);

          // Convert object to array since API returns data as object with keys
          // The API returns a dated window oldest-first; the existing design
          // sorts the page, and so does this one.
          const dataArray = newestFirst(Object.values(response.data));

          // Transform API data to match our component structure
          const transformedCases = dataArray.map((assessment, index) => {
            const warningsData = getWarnings(assessment);
            return {
              id: assessment.id || index,
              image: getVehicleImageFromAPI(assessment), // Use API image with fallback
              status: getStatusInfoFor(
                parseInt(assessment.claim_state),
                activeClient?.statusLabels,
              ).text,
              statusIcon: getStatusIcon(parseInt(assessment.claim_state)),
              statusColor: getStatusColor(parseInt(assessment.claim_state)),
              caseId:
                identityOf(assessment) ||
                getPlate(assessment) ||
                assessment.ref_num ||
                assessment.id ||
                "N/A", // Registration first, reference number only as a fallback
              refNum: assessment.ref_num || assessment.id || "N/A", // Shown beneath the plate
              fullCaseId: assessment.ref_num || assessment.id || "N/A", // Always the ref — used for navigation
              country: assessment.country_corp_id || "Unknown",
              flag: getCountryFlag(assessment.country_corp_id),
              warnings: warningsData,
              warningsOpacity:
                warningsData?.length === 1 &&
                warningsData[0].text === "No warnings"
                  ? "opacity-20"
                  : "",
              timestamp:
                formatClearlineDateTime(assessment.created_at) ||
                assessment.created_at ||
                "N/A",
              opacity:
                parseInt(assessment.claim_state) === 0 ? "opacity-50" : "",
              assessmentData: assessment, // Keep original data for reference
            };
          });

          // getAssessmentListV2 (Hegic) takes no start_date/stop_date at all,
          // so its rows are the same regardless of what's picked — the
          // calendar chip's label changed, but the list never actually
          // narrowed, which is what "the filter is out of sync" was. This
          // narrows what reaches the list client-side instead, purely so the
          // calendar does what it says. hasMoreData below still keys off the
          // raw page size — that's the true signal for whether the backend
          // has another page — not this.
          const visibleCases =
            getApiGeneration() === "v2" && range
              ? transformedCases.filter((c) =>
                  withinListRange(c.assessmentData?.created_at, range),
                )
              : transformedCases;

          if (isLoadingMore) {
            // For infinite scroll, append new data to existing cases.
            // Deduped: page_num pagination against a newest-first, actively
            // growing list can hand back a case the previous page already
            // did (a case created in between pushes everything down one
            // slot) — appended blindly, that counted and rendered twice,
            // which is how a scrolled-far-enough list's own shown count
            // ended up exceeding the server's total for the same filters.
            setCases((prevCases) => dedupeRows([...prevCases, ...visibleCases]));

            // Check if we have more data (if less than 15 items returned, we've reached the end)
            if (transformedCases?.length < 15) {
              setHasMoreData(false);
            } else {
              console.log(
                "More data available, current page:",
                currentPageRef.current,
              );
            }
          } else if (isRevalidate) {
            setCases((prev) => mergeRefreshedPage(prev, visibleCases));
            // hasMoreData is whatever the restored list already knew; a
            // first-page refresh says nothing about the end of the list.
          } else {
            // For initial load or filter changes, replace all data
            setCases(visibleCases);
            setHasMoreData(transformedCases.length >= 15);
          }
        } else {
          console.log("No data in response or response is null");
        }
      } catch (err) {
        console.error("Error fetching cases:", err);
        setError("Failed to load cases");
      } finally {
        // Don't update loading states during silent polling
        if (!isSilent) {
          if (isLoadingMore) {
            console.log(
              "✅ Resetting isLoadingMore to false - Page:",
              currentPageRef.current,
            );
            setIsLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [getCountryApiValue, getStatusApiValue], // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Initial load — from the cache when there is one, then revalidated.
   *
   * Coming back from a case used to re-fetch page one and throw away every
   * page the reader had scrolled through, so a list four pages deep came back
   * a quarter as long and the remembered scroll offset clamped to near the
   * top. The rows are restored first so the list is immediately the length it
   * was, and a background fetch then refreshes the first page, because a
   * case's status changes and a restored row must not be the final word.
   */
  useEffect(() => {
    const cached = readListCache(scrollKey);
    if (cached) {
      // The flag is a React element and cannot be stored, so it is re-derived
      // here from the country that was.
      setCases(
        cached.rows.map((row) => ({
          ...row,
          flag: getCountryFlag(row.country),
        })),
      );
      currentPageRef.current = cached.page;
      setHasMoreData(cached.hasMore);
      setLoading(false);

      // Quietly, so the restored list does not flash a spinner over itself.
      fetchCases(1, null, false, true, true);
    } else {
      fetchCases(1, null);
    }
    // The header stats are fetched by the dedicated effect below, which
    // fires once on mount the same as this one — not here, so there is one
    // place that decides what window the report asks for, not two.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Refetch the header stats whenever the filters they're scoped to change —
   * same trigger Cases.js uses for the same call. This also covers the
   * initial fetch: an effect runs once after mount regardless of its
   * dependency list, so no separate call is needed above.
   */
  useEffect(() => {
    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchAssessmentReport(dateRange);
  }, [
    startDate,
    endDate,
    selectedCountry,
    selectedWarning,
    selectedStatus,
    fetchAssessmentReport,
  ]);

  /**
   * Keep the cache in step with what is on screen.
   *
   * Written on every change rather than only on the way out: a reader can
   * leave by the browser's back button, a bookmark or a closed tab, and none
   * of those run a click handler.
   */
  useEffect(() => {
    if (loading || cases.length === 0) return;
    writeListCache(scrollKey, {
      rows: cases,
      page: currentPageRef.current,
      hasMore: hasMoreData,
    });
  }, [cases, hasMoreData, loading, scrollKey]);

  // Polling effect for processing cases
  useEffect(() => {
    // Check if any case has "Processing" status
    const hasProcessingCases = cases.some(
      (caseItem) => caseItem.status === "Processing",
    );

    if (hasProcessingCases) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Set up polling every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        // Refresh cases data without showing loading state (silent = true)
        fetchCases(
          1,
          startDate && endDate ? { startDate, endDate } : null,
          false,
          true,
        );
      }, 5000);
    } else {
      // No processing cases, clear the interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [cases, startDate, endDate, fetchCases]); // Dependencies: cases, date filters, and fetchCases

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Infinite scroll effect
  useEffect(() => {
    if (!useInfiniteScroll) return;

    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // TEMPORARY hard cap — /assessment_report's total and the list
      // endpoint's own "is there another page" signal (hasMoreData, a
      // page-came-back-short heuristic) are two different numbers that can
      // disagree, and infinite scroll would otherwise keep paginating past
      // the reported total. Only trusted under the same condition the "of Y"
      // footer below already requires (status=All, not v2 — see there for
      // why). Remove this one clause once the list API's own pagination is
      // reliable on its own.
      const reportedTotal = reportData.totalInspections;
      const totalIsComparable =
        reportedTotal > 0 && selectedStatus === "All" && getApiGeneration() !== "v2";
      const belowReportedTotal = !totalIsComparable || cases.length < reportedTotal;

      // Load more at 200px from bottom
      if (
        distanceFromBottom <= 200 &&
        !isLoadingMore &&
        !loading &&
        hasMoreData &&
        belowReportedTotal
      ) {
        console.log(
          "🚀 Triggering load more - Page:",
          currentPageRef.current + 1,
          "isLoadingMore:",
          isLoadingMore,
          "loading:",
          loading,
          "hasMoreData:",
          hasMoreData,
        );
        setIsLoadingMore(true);
        fetchCases(1, null, true, false);
      }
    };

    // Find scroll container
    const findContainer = () => {
      const container = document.getElementById("cases-list-container");
      if (container) {
        scrollContainerRef.current = container;
        container.addEventListener("scroll", handleScroll, { passive: true });
        return true;
      }
      return false;
    };

    // Try to attach listener
    if (!findContainer()) {
      // Retry with delays
      const timeouts = [50, 100, 200, 500, 1000].map((delay) =>
        setTimeout(() => !scrollContainerRef.current && findContainer(), delay),
      );

      return () => {
        timeouts.forEach(clearTimeout);
        if (scrollContainerRef.current) {
          scrollContainerRef.current.removeEventListener(
            "scroll",
            handleScroll,
          );
          scrollContainerRef.current = null;
        }
      };
    }

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener("scroll", handleScroll);
        scrollContainerRef.current = null;
      }
    };
  }, [
    useInfiniteScroll,
    isLoadingMore,
    loading,
    hasMoreData,
    cases,
    reportData.totalInspections,
    selectedStatus,
  ]);

  // Legacy sample cases data (keeping for fallback)
  /* const sampleCases = [
    {
      id: 1,
      image: car1,
      status: "Processing",
      statusIcon: loadingIcon,
      statusColor: "#1e2125",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "Last refreshed 2:20PM",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "opacity-50",
    },
    {
      id: 2,
      image: car2,
      status: "Completed",
      statusIcon: thumbsUpIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 3,
      image: car3,
      status: "AI Checked",
      statusIcon: thumbsUpIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 4,
      image: car4,
      status: "Completed",
      statusIcon: alertTriangleIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Lithuania",
      flag: <LtLithuania />,
      warnings: "Reflection/Glare/Shadow detected",
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 5,
      image: car1,
      status: "AI Checked",
      statusIcon: alertTriangleIcon,
      statusColor: "#36a149",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "VIN not readable",
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
      multilineWarnings: true,
    },
    {
      id: 6,
      image: car2,
      status: "Rejected",
      statusIcon: thumbsDownIcon,
      statusColor: "#df3822",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 7,
      image: car5,
      status: "AI Checked",
      statusIcon: alertTriangleIcon,
      statusColor: "#43b958",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: (
        <div className="font-['Noto_Sans:Regular',_sans-serif] font-normal leading-[0] relative shrink-0 text-text-primary text-base tracking-[-0.16px] w-full max-w-[406px]">
          <p className="leading-[normal] mb-0 text-base">VIN not readable</p>
          <p className="leading-[normal] mb-0 text-base">
            Number plate not readable
          </p>
          <p className="leading-[normal] text-base">
            <span>Incorrect image uploaded for front side • </span>
            <span
              className="text-[#0e53e8] cursor-pointer"
              onClick={() => setShowPopup(true)}
            >
              View
            </span>
          </p>
        </div>
      ),
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      timestampColor: "#4c5055",
      opacity: "",
      multilineWarnings: true,
      height: "h-auto min-h-[80px]",
    },
    {
      id: 8,
      image: car6,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 9,
      image: car4,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 10,
      image: car6,
      status: "Under Review",
      statusIcon: refreshIcon,
      statusColor: "#f8bc05",
      caseId: "MP6672",
      country: "Latvia",
      flag: <LvLatvia />,
      warnings: "No warnings",
      warningsOpacity: "opacity-20",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
    {
      id: 11,
      image: car7,
      status: "Awaiting Retry",
      statusIcon: hourglassIcon,
      statusColor: "#ffaa76",
      caseId: "MP6672",
      country: "Lithuania",
      flag: <LtLithuania />,
      warnings: (
        <div className="flex gap-[8px] items-center">
          <div className="overflow-clip relative shrink-0 size-[24px]">
            <img
              alt="Warning"
              className="block max-w-none size-full"
              src={alertTriangleIcon}
              style={{ filter: "hue-rotate(45deg)" }}
            />
          </div>
          <div className="flex flex-col font-['Noto_Sans:Medium',_sans-serif] font-medium justify-end leading-[0] overflow-ellipsis overflow-hidden relative shrink-0 text-text-primary text-base text-nowrap tracking-[-0.32px]">
            <p className="leading-[32px] overflow-ellipsis overflow-hidden whitespace-pre">
              Reflection/Glare/Shadow detected
            </p>
          </div>
        </div>
      ),
      warningsOpacity: "",
      timestamp: "2021-02-05 08:28:36",
      opacity: "",
    },
  ]; */

  // Filter and search functionality
  const applyFilters = useCallback(() => {
    // Skip filtering if we're in search mode (search results should not be filtered)
    if (isSearchMode) {
      return;
    }

    // For infinite scroll with server-side pagination, filter the accumulated cases
    // For traditional pagination, filter all cases
    const sourceData = cases;
    let filtered = [...sourceData];

    // Apply status filter
    if (selectedStatus !== "All") {
      filtered = filtered.filter(
        (caseItem) => caseItem.status === selectedStatus,
      );
    }

    // Apply country filter. Case-insensitive: selectedCountry now comes from
    // the client's configured list (pageConfig.filters.country.options, see
    // getUniqueCountries below) so Latvia is offered even with none loaded
    // yet — but that means it's no longer guaranteed to be spelled exactly
    // like the row's own country_corp_id. An exact match here silently wiped
    // out every row whenever the two didn't agree on case (the server had
    // already filtered correctly — countryname is normalised before it's
    // sent — so the count matched, the list didn't). Same fix already
    // applied to property's list filter (filterRowsByCountry).
    if (selectedCountry !== "All") {
      const targetCountry = selectedCountry.toLowerCase().trim();
      filtered = filtered.filter(
        (caseItem) => caseItem.country?.toLowerCase().trim() === targetCountry,
      );
    }

    // Note: Date filtering is now handled by the API, not client-side
    // Note: Search is now handled by the API via handleSearch function, not client-side

    // Update pagination for filtered results
    if (useInfiniteScroll) {
      // TEMPORARY hard cap — the scroll handler below only stops further
      // fetches once the known total is reached, but a single page already
      // in flight when that happens can still land a few rows past it. This
      // truncates what's actually rendered/counted to the reported total
      // too, so "Showing 1-X of Y" can never show X > Y. Same comparability
      // rule as the scroll handler and the "of Y" footer (status=All, not
      // v2) — remove once the list API's own pagination is reliable alone.
      const reportedTotal = reportData.totalInspections;
      const totalIsComparable =
        reportedTotal > 0 && selectedStatus === "All" && getApiGeneration() !== "v2";
      if (totalIsComparable && filtered.length > reportedTotal) {
        filtered = filtered.slice(0, reportedTotal);
      }
      // For infinite scroll, show all filtered cases from the current visible cases
      setFilteredCases(filtered);
    } else {
      // For traditional pagination, apply pagination to filtered results
      const totalItems = filtered.length;
      const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);
      setTotalPages(calculatedTotalPages);

      // Reset to first page when filters change and current page is beyond available pages
      const pageToUse = currentPage > calculatedTotalPages ? 1 : currentPage;
      if (pageToUse !== currentPage && calculatedTotalPages > 0) {
        setCurrentPage(1);
        return; // Exit early, will re-run with page 1
      }

      // Get current page items from filtered results
      const startIndex = (pageToUse - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const currentPageItems = filtered.slice(startIndex, endIndex);

      setFilteredCases(currentPageItems);
    }
  }, [
    cases,
    selectedStatus,
    selectedCountry,
    currentPage,
    itemsPerPage,
    useInfiniteScroll,
    isSearchMode,
    reportData.totalInspections,
  ]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Get unique values for dropdown options
  //
  // Deriving this from allCases (as this used to) meant an option only
  // existed once a matching case had actually loaded — so a status with no
  // case in the current date window (Awaiting Retry, most of the time)
  // never appeared at all, with no way to select it and see there genuinely
  // aren't any. Cases.js sources this from the client's own configured list
  // instead (filtersConfig.status?.options), which shows every status
  // regardless of what happens to be loaded — same source here.
  const getUniqueStatuses = useCallback(() => {
    return pageConfig.filters?.status?.options || ["All"];
  }, [pageConfig]);

  // Same reasoning as getUniqueStatuses above — a country with no case in
  // the current date window (Latvia, for Ergo, more often than not) never
  // appeared at all, with no way to select it and see there genuinely
  // aren't any. Cases.js sources this from the client's own configured list
  // instead (filtersConfig.country?.options).
  const getUniqueCountries = useCallback(() => {
    return pageConfig.filters?.country?.options || ["All"];
  }, [pageConfig]);

  // Dropdown toggle handlers
  const toggleStatusDropdown = () => {
    setShowStatusDropdown(!showStatusDropdown);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);
  };

  const toggleCountryDropdown = () => {
    setShowCountryDropdown(!showCountryDropdown);
    setShowStatusDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);
  };

  const toggleWarningDropdown = () => {
    setShowWarningDropdown(!showWarningDropdown);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
  };

  // Filter selection handlers
  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    selectedStatusRef.current = status;
    setShowStatusDropdown(false);

    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    selectedCountryRef.current = country;
    setShowCountryDropdown(false);

    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleWarningSelect = (warningCode) => {
    setSelectedWarning(warningCode);
    selectedWarningRef.current = warningCode;
    setShowWarningDropdown(false);
    setCurrentPage(1);

    currentPageRef.current = 1;
    setHasMoreData(true);

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange, false, false);
  };

  const handleDateRangeSelect = () => {
    setShowDatePicker(false);
    setCurrentPage(1);

    if (useInfiniteScroll) {
      currentPageRef.current = 1;
      setHasMoreData(true);
      setCases([]);
    }

    const dateRange = startDate && endDate ? { startDate, endDate } : null;
    fetchCases(1, dateRange);
  };

  const clearDateRange = () => {
    setStartDate("");
    setEndDate("");
    // The fetch below runs before the effect that mirrors state into the ref,
    // so clear the ref here too — otherwise "All dates" re-fetches the window
    // it just cleared.
    dateWindowRef.current = { startDate: "", endDate: "" };
    setShowDatePicker(false);
    setCurrentPage(1);

    if (useInfiniteScroll) {
      currentPageRef.current = 1;
      setHasMoreData(true);
      setCases([]);
    }

    fetchCases(1, null);
  };

  const setDateShortcut = (days) => {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - days);

    const formatDate = (date) => date.toISOString().split("T")[0];

    setStartDate(formatDate(pastDate));
    setEndDate(formatDate(today));
  };

  /**
   * Same "All Cases" / "Reset all filters" button Cases.js has — one in the
   * filter bar, one in the empty state — put back to the same defaults
   * (status, country, warning, date window, search) and refetched.
   */
  const resetAllFilters = () => {
    setSelectedStatus("All");
    setSelectedCountry("All");
    setSelectedWarning("All");
    selectedStatusRef.current = "All";
    selectedCountryRef.current = "All";
    selectedWarningRef.current = "All";

    const defaultRange = defaultListRange();
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    dateWindowRef.current = defaultRange;

    setSearchQuery("");
    setIsSearchMode(false);
    setShowStatusDropdown(false);
    setShowCountryDropdown(false);
    setShowWarningDropdown(false);
    setShowDatePicker(false);

    setCurrentPage(1);
    currentPageRef.current = 1;
    setHasMoreData(true);

    if (useInfiniteScroll) {
      setCases([]);
    }

    fetchCases(1, defaultRange);
  };

  // Search by ref_num using the dedicated search API
  const handleSearch = async (query) => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setIsSearchMode(false);
      setError(null);
      const dateRange = startDate && endDate ? { startDate, endDate } : null;
      fetchCases(1, dateRange, false, false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setIsSearchMode(true);
      setCurrentPage(1);
      currentPageRef.current = 1;
      setHasMoreData(false);

      // source_name deliberately omitted: searchCases resolves it from the
      // logged-in client. It was hardcoded to "ergobaltic" here, which predates
      // multi-client support and returned nothing for Bajaj or Hegic.
      const searchResults = await searchCases({
        ref_num: trimmedQuery,
        country_id: "",
        countryname: "",
      });

      let dataArray = [];

      if (searchResults) {
        if (Array.isArray(searchResults)) {
          dataArray = searchResults;
        } else if (searchResults.data && Array.isArray(searchResults.data)) {
          dataArray = searchResults.data;
        } else if (typeof searchResults === "object") {
          dataArray = Object.values(searchResults);
        }
      }

      if (dataArray.length > 0) {
        const transformedSearchResults = dataArray.map((assessment, index) => {
          const warningsData = getWarnings(assessment);
          return {
            id: assessment.id || index,
            image: getVehicleImageFromAPI(assessment),
            status: getStatusInfoFor(
              parseInt(assessment.claim_state),
              activeClient?.statusLabels,
            ).text,
            statusIcon: getStatusIcon(parseInt(assessment.claim_state)),
            statusColor: getStatusColor(parseInt(assessment.claim_state)),
            caseId:
              getPlate(assessment) ||
              assessment.ref_num ||
              assessment.id ||
              "N/A",
            refNum: assessment.ref_num || assessment.id || "N/A",
            fullCaseId: assessment.ref_num || assessment.id || "N/A",
            country: assessment.country_corp_id || "Unknown",
            flag: getCountryFlag(assessment.country_corp_id),
            warnings: warningsData,
            warningsOpacity:
              warningsData.length === 1 &&
              warningsData[0].text === "No warnings"
                ? "opacity-20"
                : "",
            timestamp:
              formatClearlineDateTime(assessment.created_at) ||
              assessment.created_at ||
              "N/A",
            opacity: parseInt(assessment.claim_state) === 0 ? "opacity-50" : "",
            assessmentData: assessment,
          };
        });

        // /search takes no date params at all, so it answers across every
        // date regardless of what's picked — the same reason getBreakinCases
        // needs withinListRange applied client-side. A search is still a
        // filter on this page; it shouldn't be the one way to see a case the
        // date range is meant to exclude.
        const dateRange = startDate && endDate ? { startDate, endDate } : null;
        const dateFilteredResults = dateRange
          ? transformedSearchResults.filter((row) =>
              withinListRange(row.assessmentData?.created_at, dateRange),
            )
          : transformedSearchResults;

        setCases(dateFilteredResults);
        setFilteredCases(dateFilteredResults);
        setTotalPages(dateFilteredResults.length > 0 ? 1 : 0);
        if (dateFilteredResults.length === 0) {
          setError(
            dateRange
              ? `No results found for "${trimmedQuery}" in ${dateRange.startDate} → ${dateRange.endDate}`
              : `No results found for "${trimmedQuery}"`,
          );
        }
      } else {
        setCases([]);
        setFilteredCases([]);
        setTotalPages(0);
        setError(`No results found for "${trimmedQuery}"`);
      }
    } catch (searchError) {
      console.error("Search error:", searchError);
      setError("Failed to search cases");
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────
  const totalInspections = reportData.totalInspections;

  // A claim can't have been received tomorrow — caps both date inputs below
  // so the picker can't be set to a range no case will ever match.
  const todayIso = new Date().toISOString().split("T")[0];

  const dateFilterLabel =
    startDate && endDate ? `${startDate} → ${endDate}` : "All dates";

  const activeWarningLabel =
    selectedWarning === "All"
      ? "Any"
      : visibleWarningCodes.find((w) => w.code === selectedWarning)
          ?.description || "Any";

  // A row's warnings collapse to one amber chip; the rest open the modal.
  const getRowWarnings = (caseItem) => {
    if (!Array.isArray(caseItem.warnings)) {
      const text = caseItem.warnings;
      return !text || text === "No warnings" ? [] : [{ message: text }];
    }
    return caseItem.warnings.filter(
      (warning) => warning?.message && warning.message !== "No warnings",
    );
  };

  const rangeStart = filteredCases.length === 0 ? 0 : 1;
  const rangeEnd = filteredCases.length;

  // ── Shared class fragments ─────────────────────────────────────────────
  // With both country and warnings hidden (Bajaj, and now Hegic), caseId was
  // the only flexible track left, so it alone (or, briefly, a spacer standing
  // in for it) absorbed all the width the other two columns used to share —
  // on anything wider than a laptop that read as a wall of blank space next
  // to a short case ID, whether that blank space was inside caseId's own cell
  // or split into an invisible neighbour. Making every remaining column
  // flexible instead spreads the same leftover width across all four, each
  // still floored at its original size, so no single cell reads as "empty."
  // Ergo still has both, so its layout is untouched fixed-px tracks — this
  // only kicks in once both are gone.
  const compactCaseId = !showCountry && !showWarningsColumn;
  const gridCols = compactCaseId
    ? "minmax(132px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr)"
    : [
        "132px", // vehicle
        "150px", // status
        "1fr", // caseId
        showCountry && "120px",
        showWarningsColumn && "1.4fr",
        "150px", // timestamp
      ]
        .filter(Boolean)
        .join(" ");
  const filterPillClass =
    "flex items-center gap-[7px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint";
  const dropdownPanelClass =
    "cl-scroll absolute left-0 top-[calc(100%+6px)] z-dropdown min-w-[200px] overflow-y-auto rounded-tile border border-cl-card bg-cl-surface py-1 shadow-[0_8px_24px_rgba(19,23,32,0.10)]";
  const dropdownItemClass =
    "cursor-pointer px-3 py-2 text-left text-[12.5px] text-cl-secondary transition-colors hover:bg-cl-tint hover:text-cl-accent";

  const pageShellClass =
    "flex h-full min-h-0 w-full flex-col bg-cl-bg px-4 py-4 font-clearline text-cl-text sm:px-8 sm:py-[26px]";

  // Header is shared by the loaded, empty and error states.
  //
  // "Copy inspection link" / "New Case" hidden for now — the inspection
  // link/flow they point at (PORTAL_BASE_URL/inspection) isn't a service
  // this app can vouch for yet. Removed rather than left disabled or
  // commented out, so there's nothing half-rendered while it's not ready;
  // see git history for handleCopyInspectionLink/handleNewInspection when
  // it is.
  const header = (
    <div className="mb-5 flex flex-col items-start gap-3 lg:flex-row lg:justify-between lg:gap-4">
      <div>
        <h1 className="text-[22px] font-bold leading-tight tracking-[-0.02em] text-cl-text">
          Cases
        </h1>
        {/* Hidden once the visible list is empty — the report's own total
            (scoped to status/country/warning/date, not to the list's exact
            rows) can disagree with "0 shown" and reads as a contradiction
            sitting right above "No cases found". */}
        {!(!loading && filteredCases.length === 0) && (
          <p className="mt-[3px] text-[12.5px] text-cl-muted">
            {reportLoading
              ? "Loading inspection totals…"
              : reportData.incorrectImages > 0
                ? `A total of ${totalInspections.toLocaleString()} inspections, ${reportData.incorrectImages.toLocaleString()} failed due to images not meeting inspection guidelines`
                : `A total of ${totalInspections.toLocaleString()} inspections`}
          </p>
        )}
      </div>
    </div>
  );

  // Show error state
  if (error) {
    return (
      <div className={pageShellClass}>
        {header}
        <div className="flex flex-1 items-center justify-center rounded-card border border-cl-card bg-cl-surface">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-pill bg-[#fbe9e9]">
              <AlertTriangle className="h-5 w-5 text-[#c22f2f]" />
            </div>
            <p className="text-[14px] font-semibold text-cl-text">{error}</p>
            <p className="mt-1 text-[12.5px] text-cl-muted">
              Check your connection, then try loading the case list again.
            </p>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setSearchQuery("");
                setIsSearchMode(false);
                currentPageRef.current = 1;
                setHasMoreData(true);
                fetchCases(
                  1,
                  startDate && endDate ? { startDate, endDate } : null,
                );
              }}
              className="mt-4 rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageShellClass}>
      {header}

      {/* Filter bar */}
      <div className="mb-[18px] flex flex-wrap items-center gap-[10px]">
        {/* All Cases — resets every filter to its default, same button
            Cases.js has in the same spot. */}
        <button
          type="button"
          onClick={resetAllFilters}
          className="flex-none rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
        >
          All Cases
        </button>

        {/* Search */}
        <div className="relative flex w-full max-w-[320px] flex-1 items-center gap-[9px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] focus-within:border-cl-accent">
          <Search className="h-[14px] w-[14px] flex-none text-cl-faint" />
          <input
            type="text"
            placeholder="Search case ID, VIN, plate…"
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              // Only clear search results if input is completely cleared
              if (value.trim() === "") {
                handleSearch("");
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch(searchQuery);
              }
            }}
            className="min-w-0 flex-1 bg-transparent text-[13px] text-cl-text outline-none placeholder:text-cl-faint"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                handleSearch("");
              }}
              title="Clear search"
              className="flex-none text-cl-faint transition-colors hover:text-cl-secondary"
            >
              <X className="h-[14px] w-[14px]" />
            </button>
          )}
        </div>

        {/* Status */}
        <div ref={statusDropdownRef} className="relative">
          <button
            type="button"
            onClick={toggleStatusDropdown}
            className={filterPillClass}
          >
            Status
            <span className="font-semibold text-cl-accent">
              {selectedStatus === "All" ? "All" : selectedStatus}
            </span>
            <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
          </button>
          {showStatusDropdown && (
            <div className={`${dropdownPanelClass} max-h-[320px]`}>
              {getUniqueStatuses().map((status) => (
                <div
                  key={status}
                  className={`${dropdownItemClass} flex items-center gap-[8px]`}
                  onClick={() => handleStatusSelect(status)}
                >
                  {status === "All" ? (
                    <span className="size-[14px] flex-none" />
                  ) : (
                    <img
                      src={getStatusIcon(getStatusCodeFromText(status))}
                      alt=""
                      className="size-[14px] flex-none"
                    />
                  )}
                  {status}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Country */}
        {showCountry && (
          <div ref={countryDropdownRef} className="relative">
            <button
              type="button"
              onClick={toggleCountryDropdown}
              className={filterPillClass}
            >
              Country
              <span className="font-semibold text-cl-accent">
                {selectedCountry === "All" ? "All" : selectedCountry}
              </span>
              <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
            </button>
            {showCountryDropdown && (
              <div className={`${dropdownPanelClass} max-h-[320px]`}>
                {getUniqueCountries().map((country) => (
                  <div
                    key={country}
                    className={`${dropdownItemClass} flex items-center gap-[8px]`}
                    onClick={() => handleCountrySelect(country)}
                  >
                    {country === "All" ? (
                      <span className="h-[13px] w-[18px] flex-none" />
                    ) : (
                      getCountryFlag(country)
                    )}
                    {country}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Warnings */}
        {showWarningsFilter && (
          <div ref={warningDropdownRef} className="relative">
            <button
              type="button"
              onClick={toggleWarningDropdown}
              className={filterPillClass}
            >
              Warnings
              <span className="max-w-[160px] truncate font-semibold text-cl-accent">
                {activeWarningLabel}
              </span>
              <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
            </button>
            {showWarningDropdown && (
              <div
                className={`${dropdownPanelClass} max-h-[380px] min-w-[320px]`}
              >
                {visibleWarningCodes.map((warning) => (
                  <div
                    key={warning.code}
                    className={dropdownItemClass}
                    onClick={() => handleWarningSelect(warning.code)}
                  >
                    {warning.description}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Date range */}
        <div ref={datePickerRef} className="relative">
          <button
            type="button"
            onClick={toggleDatePicker}
            className={filterPillClass}
          >
            <span
              className={
                startDate && endDate ? "font-semibold text-cl-accent" : ""
              }
            >
              {dateFilterLabel}
            </span>
            <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
          </button>
          {showDatePicker && (
            <div className="absolute left-0 top-[calc(100%+6px)] z-dropdown w-[300px] rounded-tile border border-cl-card bg-cl-surface p-4 shadow-[0_8px_24px_rgba(19,23,32,0.10)]">
              <div className="mb-3 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                Quick select
              </div>
              <div className="mb-4 flex gap-2">
                {[
                  { days: 1, label: "Last day" },
                  { days: 7, label: "Last 7 days" },
                  { days: 30, label: "Last 30 days" },
                ].map((shortcut) => (
                  <button
                    key={shortcut.days}
                    type="button"
                    onClick={() => setDateShortcut(shortcut.days)}
                    className="flex-1 rounded-tile border border-cl-control bg-cl-surface px-2 py-[6px] text-[11px] font-medium text-cl-secondary transition-colors hover:border-cl-accent hover:text-cl-accent"
                  >
                    {shortcut.label}
                  </button>
                ))}
              </div>

              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                From
              </label>
              <input
                type="date"
                value={startDate}
                max={todayIso}
                onChange={(e) => setStartDate(e.target.value)}
                className="cl-date-input mb-3 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
              />

              <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                To
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={todayIso}
                onChange={(e) => setEndDate(e.target.value)}
                className="cl-date-input mb-4 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
              />

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleDateRangeSelect}
                  className="flex-1 rounded-tile bg-cl-accent px-3 py-2 text-[12px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={clearDateRange}
                  className="flex-1 rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="ml-auto text-[12px] text-cl-faint">
          Sort: Newest first
        </div>
      </div>

      {/* Table card */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-cl-card bg-cl-surface">
        {/* Header row */}
        <div
          /* Hidden below lg. The header sits OUTSIDE the body scroller, so
             when the grid overflowed a phone the rows panned and the header
             could not follow — headings drifted out of line with their own
             columns. A stacked card list needs no column headers, which
             removes the mismatch rather than trying to sync two scrollers. */
          className="hidden flex-none items-center border-b border-cl-head px-4 py-[10px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-cl-label lg:grid"
          style={{ gridTemplateColumns: gridCols }}
        >
          <div>{columnLabels.vehicle}</div>
          <div>{columnLabels.status}</div>
          <div>{columnLabels.caseId}</div>
          {showCountry && <div>{columnLabels.country}</div>}
          {showWarningsColumn && <div>{columnLabels.warnings}</div>}
          <div className="text-right">{columnLabels.timestamp}</div>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="text-center">
              <img
                src={loadingIcon}
                alt=""
                className="mx-auto mb-3 h-7 w-7 animate-spin"
              />
              <p className="text-[12.5px] text-cl-muted">Loading cases…</p>
            </div>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <div className="max-w-sm text-center">
              <p className="text-[14px] font-semibold text-cl-text">
                No cases found
              </p>
              <p className="mt-1 text-[12.5px] text-cl-muted">
                {isSearchMode
                  ? "No inspections match this search. Clear it to see the full list."
                  : "No inspections match the current filters."}
              </p>
              <button
                type="button"
                onClick={resetAllFilters}
                className="mt-4 rounded-control bg-cl-accent px-5 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
              >
                {isSearchMode ? "View All Cases" : "Reset all filters"}
              </button>
            </div>
          </div>
        ) : (
          <div
            id="cases-list-container"
            className="cl-scroll min-h-0 flex-1 overflow-y-auto"
          >
            {filteredCases.map((caseItem) => {
              // getStatusInfoFor's actual text for claim_state 0/1/2 is
              // "In-Process" — this compared against "Processing", a string
              // that name never produces, so the row always got a real link
              // and opened the detail page for a case still being processed.
              const isProcessing = caseItem.status === "In-Process";
              const chip = getClearlineStatusChip(caseItem.status);
              const rowWarnings = getRowWarnings(caseItem);
              const [firstWarning, ...restWarnings] = rowWarnings;

              // Same reference the click used: never caseId, which holds the
              // plate. A processing row gets no href and stays a plain div.
              const rowHref = isProcessing
                ? null
                : withCountryParam(
                    caseHrefFor(
                      caseItem.assessmentData?.ref_num || caseItem.fullCaseId,
                      detailsBasePath,
                    ),
                    caseItem.country,
                  );

              return (
                <div
                  key={caseItem.id}
                  className={`relative flex flex-col gap-[6px] border-b border-cl-row px-4 py-3 transition-colors lg:grid lg:items-center lg:gap-0 lg:py-[10px] ${
                    isProcessing
                      ? "cursor-default opacity-60"
                      : "cursor-pointer hover:bg-cl-hover"
                  }`}
                  style={{ gridTemplateColumns: gridCols }}
                  onMouseEnter={(event) => handleRowHoverStart(caseItem, event)}
                  onMouseLeave={handleRowHoverEnd}
                >
                  {/* The row's link, stretched over it.

                      An anchor rather than an onClick so the browser gives us
                      cmd/ctrl-click and middle-click for free — and stretched
                      rather than wrapping the row because the warnings cell
                      contains real buttons, and interactive content inside an
                      <a> is invalid HTML that browsers resolve however they
                      like. The chips sit above it on z-[1] and keep working.
                      target="_blank" makes a plain click open a new tab too,
                      same as every other list in the app — which also means
                      no router `state` reaches that tab, so the detail page's
                      Back button correctly hides itself (see
                      src/utils/backNavigation.js: no state and no history
                      means nowhere to go back to). */}
                  {rowHref && (
                    <Link
                      to={rowHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        rememberScroll();
                        handleRowClick(caseItem, event);
                      }}
                      aria-label={`Open case ${caseItem.fullCaseId || caseItem.caseId} in a new tab`}
                      className="absolute inset-0 z-0"
                    />
                  )}
                  {/* Vehicle, or the property */}
                  <div>
                    {selfFetchesThumbnail ? (
                      <CaseThumb
                        refNum={caseItem.fullCaseId}
                        className="block h-[58px] w-[104px] rounded-thumb bg-cl-bg object-cover"
                      />
                    ) : (
                      <img
                        src={caseItem.image}
                        alt=""
                        loading="lazy"
                        className="block h-[58px] w-[104px] rounded-thumb bg-cl-bg object-cover"
                      />
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <span
                      className="inline-flex items-center gap-[6px] rounded-pill px-[10px] py-1 text-[11.5px] font-semibold"
                      style={{ background: chip.bg, color: chip.fg }}
                    >
                      <span
                        className="h-[6px] w-[6px] rounded-pill"
                        style={{ background: chip.fg }}
                      />
                      {caseItem.status}
                    </span>
                  </div>

                  {/* Case ID + plate */}
                  <div className="min-w-0 pr-3">
                    <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em] text-cl-text">
                      {caseItem.caseId}
                    </div>
                    {caseItem.refNum && caseItem.refNum !== caseItem.caseId && (
                      <div className="mt-[2px] truncate text-[11px] font-normal text-cl-faint">
                        {caseItem.refNum}
                      </div>
                    )}
                  </div>

                  {/* Country */}
                  {showCountry && (
                    <div className="flex min-w-0 items-center gap-[7px] pr-3 text-[12.5px] text-cl-secondary">
                      {caseItem.flag}
                      <span className="truncate">{caseItem.country}</span>
                    </div>
                  )}

                  {/* Warnings — needs only to clear the stretched row link's
                      z-0 to stay clickable; z-10 happened to be the exact
                      value the shared --z-dropdown token uses (design-tokens.css),
                      so an open Warnings/Country/Date filter dropdown lost the
                      stacking tie to whichever row painted after it in the DOM
                      and rendered underneath these chips instead of over them. */}
                  {showWarningsColumn && (
                    <div className="relative z-[1] flex min-w-0 flex-wrap items-center gap-[6px] pr-3">
                      {rowWarnings.length === 0 ? (
                        <span className="text-[12px] text-cl-disabled">None</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            title={firstWarning.message}
                            onClick={(e) => {
                              e.stopPropagation();
                              openAllWarningsModal(rowWarnings);
                            }}
                            className="inline-flex min-w-0 items-center gap-[6px] rounded-[7px] bg-[#fdf3df] px-[9px] py-1 text-left text-[11.5px] font-medium text-[#8a5c07] transition-opacity hover:opacity-85"
                          >
                            <AlertTriangle className="h-[11px] w-[11px] flex-none" />
                            <span className="truncate">
                              {firstWarning.message}
                            </span>
                          </button>
                          {restWarnings.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAllWarningsModal(rowWarnings);
                              }}
                              className="flex-none rounded-[7px] bg-cl-bg px-[7px] py-1 text-[11px] font-semibold text-cl-muted transition-colors hover:bg-cl-tint hover:text-cl-accent"
                            >
                              +{restWarnings.length}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Received */}
                  <div className="text-right text-[12px] text-cl-muted">
                    {caseItem.timestamp}
                  </div>
                </div>
              );
            })}

            {/* Infinite-scroll footer */}
            {useInfiniteScroll && isLoadingMore && (
              <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-cl-muted">
                <img
                  src={loadingIcon}
                  alt=""
                  className="h-4 w-4 animate-spin"
                />
                Loading more cases…
              </div>
            )}
            {useInfiniteScroll &&
              !hasMoreData &&
              !isLoadingMore &&
              filteredCases.length > 0 && (
                <div className="py-6 text-center text-[12px] text-cl-disabled">
                  You've reached the end of the list
                </div>
              )}
          </div>
        )}

        {/* Card footer */}
        <div className="flex flex-none items-center justify-between border-t border-cl-head px-4 py-3 text-[12px] text-cl-label">
          <div>
            {loading
              ? "Loading…"
              : `Showing ${rangeStart}–${rangeEnd}${
                  // totalInspections is the report's total for date/country/
                  // warning only — status stays a client-side filter over
                  // whatever pages are loaded (see applyFilters), so once a
                  // specific status is selected the two numbers are counting
                  // different things and pairing them as "X of Y" is not a
                  // fact this page can vouch for.
                  //
                  // v2 (Hegic): totalInspections is deliberately a lifetime
                  // count (see fetchAssessmentReport) — the same one the live
                  // Hegic portal itself shows — not a total for whatever
                  // window the list's own date filter is currently asking
                  // for. Pairing "Showing 1-98" against "of 1439" would be
                  // exactly the same false claim as before, just the other
                  // direction, so v2 never gets an "of Y" here at all.
                  totalInspections &&
                  selectedStatus === "All" &&
                  getApiGeneration() !== "v2"
                    ? ` of ${totalInspections.toLocaleString()}`
                    : ""
                }`}
          </div>

          {!useInfiniteScroll && totalPages > 0 && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`rounded-[7px] px-[10px] py-[5px] transition-colors ${
                  currentPage === 1
                    ? "cursor-not-allowed text-cl-disabled"
                    : "text-cl-secondary hover:bg-cl-tint hover:text-cl-accent"
                }`}
              >
                Prev
              </button>

              {(() => {
                const pageNumbers = [];
                const maxVisiblePages = 5;

                if (totalPages <= maxVisiblePages) {
                  for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
                } else if (currentPage <= 3) {
                  pageNumbers.push(1, 2, 3, 4, "…", totalPages);
                } else if (currentPage >= totalPages - 2) {
                  pageNumbers.push(
                    1,
                    "…",
                    totalPages - 3,
                    totalPages - 2,
                    totalPages - 1,
                    totalPages,
                  );
                } else {
                  pageNumbers.push(
                    1,
                    "…",
                    currentPage - 1,
                    currentPage,
                    currentPage + 1,
                    "…",
                    totalPages,
                  );
                }

                return pageNumbers.map((page, index) =>
                  page === "…" ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-[10px] py-[5px] text-cl-meta"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      onClick={() => handlePageChange(page)}
                      className={`rounded-[7px] px-[10px] py-[5px] transition-colors ${
                        currentPage === page
                          ? "bg-cl-accent font-semibold text-white"
                          : "text-cl-secondary hover:bg-cl-tint hover:text-cl-accent"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                );
              })()}

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`rounded-[7px] px-[10px] py-[5px] transition-colors ${
                  currentPage === totalPages
                    ? "cursor-not-allowed text-cl-disabled"
                    : "text-cl-secondary hover:bg-cl-tint hover:text-cl-accent"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────── */}

      {/* All warnings modal */}
      {showAllWarningsModal && (
        <div
          className="fixed inset-0 z-modal flex items-center justify-center bg-[rgba(19,23,32,0.55)] p-4"
          onClick={closeAllWarningsModal}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-card border border-cl-card bg-cl-surface"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-none items-center justify-between border-b border-cl-head px-5 py-4">
              <h2 className="text-[15px] font-bold tracking-[-0.01em] text-cl-text">
                {selectedModalImage
                  ? "Warning detail"
                  : `Warnings (${allWarningsData.length})`}
              </h2>
              <div className="flex items-center gap-2">
                {selectedModalImage && (
                  <button
                    type="button"
                    className="rounded-tile px-3 py-[6px] text-[12px] font-medium text-cl-secondary transition-colors hover:bg-cl-hover"
                    onClick={() => {
                      setSelectedModalImage(null);
                      // Restore scroll position after the list re-renders
                      setTimeout(() => {
                        if (warningsScrollRef.current) {
                          warningsScrollRef.current.scrollTop =
                            savedScrollPosition;
                        }
                      }, 10);
                    }}
                  >
                    ← Back to list
                  </button>
                )}
                <button
                  type="button"
                  aria-label="Close"
                  className="flex h-8 w-8 items-center justify-center rounded-pill text-cl-faint transition-colors hover:bg-cl-hover hover:text-cl-secondary"
                  onClick={closeAllWarningsModal}
                >
                  <X className="h-[16px] w-[16px]" />
                </button>
              </div>
            </div>

            <div
              ref={warningsScrollRef}
              className="cl-scroll min-h-0 flex-1 overflow-y-auto"
            >
              {selectedModalImage ? (
                <div className="flex min-h-[420px] items-center justify-center p-5">
                  <img
                    src={selectedModalImage}
                    alt="Enlarged warning detail"
                    className="max-h-[calc(90vh-220px)] max-w-full rounded-tile object-contain"
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-3 p-5">
                  {allWarningsData.map((warning, index) => (
                    <div
                      key={index}
                      className="rounded-card border border-cl-card bg-cl-bg p-4"
                    >
                      <div className="flex items-start gap-[10px]">
                        <span className="mt-[2px] flex h-5 w-5 flex-none items-center justify-center rounded-pill bg-[#fdf3df]">
                          <AlertTriangle className="h-[11px] w-[11px] text-[#8a5c07]" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                            Warning {index + 1}
                          </div>
                          <p className="mt-[3px] text-[13px] leading-[1.5] text-cl-text">
                            {warning.message}
                          </p>
                        </div>
                      </div>

                      {warning.sidesData && warning.sidesData.length > 0 && (
                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {warning.sidesData.map((side, sideIndex) => (
                            <div
                              key={sideIndex}
                              className="overflow-hidden rounded-tile border border-cl-card bg-cl-surface"
                            >
                              {side.imageUrl ? (
                                <button
                                  type="button"
                                  className="block w-full"
                                  onClick={() => {
                                    if (warningsScrollRef.current) {
                                      setSavedScrollPosition(
                                        warningsScrollRef.current.scrollTop,
                                      );
                                    }
                                    setSelectedModalImage(side.imageUrl);
                                  }}
                                >
                                  <img
                                    src={side.imageUrl}
                                    alt={`Warning for ${side.name}`}
                                    className="h-40 w-full object-cover transition-opacity hover:opacity-90"
                                  />
                                </button>
                              ) : (
                                <div className="flex h-40 w-full items-center justify-center bg-cl-bg text-[11px] text-cl-faint">
                                  No image available
                                </div>
                              )}
                              <div className="px-3 py-[7px] text-[11.5px] font-semibold text-cl-secondary">
                                {side.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-none justify-end border-t border-cl-head px-5 py-4">
              <button
                type="button"
                className="rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
                onClick={closeAllWarningsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
